import type { ReactNode } from "react";

/**
 * The weekly pack, rendered as the document it is.
 *
 * It was shown as its own Markdown source in a monospace block — hashes,
 * asterisks, and the position-at-a-glance table as pipe-delimited text —
 * on a screen headed "what the commercial meeting needs to decide".
 * Everything it said was correct and none of it was presented as a
 * document.
 *
 * This is not a general Markdown renderer and does not try to be. It
 * handles exactly what `engine/surfaces/pack.py` emits: headings, one
 * blockquote, pipe tables, dash lists, paragraphs, and `**bold**` inline.
 * Anything else falls through as text rather than being half-parsed —
 * a renderer that silently mangles an unknown construct is worse than one
 * that shows it plainly. `tools/check_pack_renders.py` fails the build if
 * the generator starts emitting something this does not cover.
 */
export function PackDocument({ markdown }: { markdown: string }) {
  return <div className="packdoc">{blocks(withoutTitle(markdown).split("\n"))}</div>;
}

/**
 * Drops the pack's own `# ` title.
 *
 * The screen already carries it as its heading, and the generated document
 * repeats it because it is also written to a file, where it needs one.
 */
export function withoutTitle(markdown: string): string {
  const lines = markdown.split("\n");
  const first = lines.findIndex((l) => l.trim());
  if (first < 0 || !/^#\s+/.test(lines[first] ?? "")) return markdown;
  return lines.slice(first + 1).join("\n");
}

function blocks(lines: string[]): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    if (!line.trim()) { i++; continue; }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = (heading[1] ?? "").length;
      const text = inline(heading[2] ?? "");
      out.push(
        level === 1 ? <h2 key={key++} className="packh1">{text}</h2>
        : level === 2 ? <h3 key={key++} className="packh2">{text}</h3>
        : <h4 key={key++} className="packh3">{text}</h4>,
      );
      i++;
      continue;
    }

    if (line.startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && (lines[i] ?? "").startsWith(">")) {
        quote.push((lines[i] ?? "").replace(/^>\s?/, ""));
        i++;
      }
      out.push(<blockquote key={key++}>{inline(quote.join(" "))}</blockquote>);
      continue;
    }

    if (line.startsWith("|")) {
      const rows: string[] = [];
      while (i < lines.length && (lines[i] ?? "").startsWith("|")) {
        rows.push(lines[i] ?? "");
        i++;
      }
      out.push(<PackTable key={key++} rows={rows} />);
      continue;
    }

    if (/^\s*-\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\s*-\s+/, ""));
        i++;
      }
      out.push(
        <ul key={key++}>
          {items.map((item, n) => <li key={n}>{inline(item)}</li>)}
        </ul>,
      );
      continue;
    }

    const para: string[] = [];
    while (i < lines.length && (lines[i] ?? "").trim()
           && !/^[#>|]/.test(lines[i] ?? "") && !/^\s*-\s+/.test(lines[i] ?? "")) {
      para.push(lines[i] ?? "");
      i++;
    }
    out.push(<p key={key++}>{inline(para.join(" "))}</p>);
  }
  return out;
}

/** A pipe table. The separator row carries the alignment and is not data. */
function PackTable({ rows }: { rows: string[] }) {
  const cells = (row: string) =>
    row.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

  const isRule = (row: string) => /^\|[\s:|-]+\|$/.test(row);
  const ruleAt = rows.findIndex(isRule);
  const at = (n: number) => rows[n] ?? "";
  // The pack writes a header row for some tables and an empty one for the
  // position summary, where the two columns are label and figure.
  const header = ruleAt > 0 ? cells(at(ruleAt - 1)) : null;
  const aligns: Array<"left" | "right"> = ruleAt >= 0
    ? cells(at(ruleAt)).map((c) => (c.endsWith(":") ? "right" : "left"))
    : [];
  const body = rows.filter((r, n) => !isRule(r) && n !== (ruleAt > 0 ? ruleAt - 1 : -1));

  const labelled = header?.some((h) => h.length > 0);

  return (
    <div className="tablewrap">
      <table className="packtable">
        {labelled && (
          <thead>
            <tr>{header!.map((h, n) => (
              <th key={n} style={{ textAlign: aligns[n] ?? "left" }}>{h}</th>
            ))}</tr>
          </thead>
        )}
        <tbody>
          {body.map((row, n) => (
            <tr key={n}>
              {cells(row).map((c, m) => (
                <td key={m} style={{ textAlign: aligns[m] ?? "left" }}>{inline(c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * `**bold**` and `_italic_`. Everything else is text, deliberately.
 *
 * The pack uses italic for its asides — "_Nothing inside seven days._",
 * "_None._" — so leaving it out meant a client read the underscores.
 */
export function inline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|_[^_]+_)/g);
  return parts.map((part, n) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={n}>{part.slice(2, -2)}</strong>;
    }
    if (/^_[^_]+_$/.test(part)) {
      return <em key={n}>{part.slice(1, -1)}</em>;
    }
    return <span key={n}>{part}</span>;
  });
}
